/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/toolslib/src/utils/id.util.ts
 */

import { v4 as uuidV4 } from 'uuid';

export { uuidV4 };

/**
 * Simple hash function for task content
 * This creates a deterministic hash based on the task name and description
 */
export function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Generates a deterministic UUID-like string based on a seed string
 * This provides better distribution and longer, more unique results than simpleHash
 * @param seed - The string to use as a seed
 * @returns A UUID-like string (32 characters, no hyphens)
 */
export function seededUuid(seed: string): string {
  // Create a more robust hash from the seed
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash + char) >>> 0; // Use unsigned 32-bit
  }

  // Use the hash to seed a simple pseudo-random number generator
  const random = (a: number, _b: number) => {
    const x = Math.sin(a) * 10000;
    return x - Math.floor(x);
  };

  // Generate 16 bytes (32 hex characters) using the seed
  const bytes: number[] = [];
  for (let i = 0; i < 16; i++) {
    const rand = random(hash + i, i);
    bytes.push(Math.floor(rand * 256));
  }

  // Convert to hex string
  return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generates a deterministic UUID (with hyphens) based on a seed string
 * @param seed - The string to use as a seed
 * @returns A standard UUID format string (8-4-4-4-12 characters)
 */
export function seededUuidFormatted(seed: string): string {
  const uuid = seededUuid(seed);
  return [
    uuid.slice(0, 8),
    uuid.slice(8, 12),
    uuid.slice(12, 16),
    uuid.slice(16, 20),
    uuid.slice(20, 32)
  ].join('-');
}

/**
 * Generates a deterministic UUID for Stripe customer reference
 * This adds salt to the user ID to prevent exposing Gmail/OAuth provider IDs
 * @param userId - The user's ID (e.g., from OAuth provider)
 * @returns A standard UUID format string safe for use with Stripe
 */
export function getStripeCustomerReferenceId(userId: string): string {
  // TODO: Implement this when ready
  return userId;
}

/**
 * Verifies if a Stripe customer reference ID matches a given user ID
 * @param hashedReferenceId - The hashed reference ID from Stripe
 * @param userId - The user ID to verify against
 * @returns True if the hashed ID matches the user ID
 */
export function verifyStripeCustomerReferenceId(
  hashedReferenceId: string,
  userId: string
): boolean {
  return getStripeCustomerReferenceId(userId) === hashedReferenceId;
}
